// Shared contracts for the web app and the Worker. The Python engine uses the same schemas
// through the generated models in python/unitly_contracts.
import { Validator, type OutputUnit } from "@cfworker/json-schema";

import commonSchema from "../schema/common.schema.json";
import projectModelSchema from "../schema/project-model.schema.json";
import parameterSetSchema from "../schema/parameter-set.schema.json";
import layerInventorySchema from "../schema/layer-inventory.schema.json";
import planPreviewSchema from "../schema/plan-preview.schema.json";
import apiSchema from "../schema/api.schema.json";
import preset3bed from "../presets/preset-3bed-2-per-floor.json";
import malagaRules from "../presets/regulations/malaga-pgou-cte.json";

import type { ParameterSet, Rule } from "./types";

export * from "./types";

export const schemas = {
  common: commonSchema,
  projectModel: projectModelSchema,
  parameterSet: parameterSetSchema,
  layerInventory: layerInventorySchema,
  planPreview: planPreviewSchema,
  api: apiSchema,
} as const;

type SchemaDoc = { $id: string; [key: string]: unknown };

function makeValidator(schema: SchemaDoc, pointer?: string): Validator {
  const root = pointer ? { $ref: `${schema.$id}#${pointer}` } : schema;
  const v = new Validator(root as object, "2020-12", false);
  for (const s of Object.values(schemas)) if (pointer || s.$id !== schema.$id) v.addSchema(s as object);
  return v;
}

export type ValidationResult = { valid: true } | { valid: false; errors: string[] };

function run(v: Validator, value: unknown): ValidationResult {
  const r = v.validate(value);
  if (r.valid) return { valid: true };
  const errors = r.errors
    .filter((e: OutputUnit) => e.keyword !== "properties" && e.keyword !== "$ref" && e.keyword !== "items")
    .map((e: OutputUnit) => `${e.instanceLocation || "#"}: ${e.error}`);
  return { valid: false, errors: errors.length ? errors : r.errors.map((e: OutputUnit) => e.error) };
}

const cache = new Map<string, Validator>();
function cached(key: string, schema: SchemaDoc, pointer?: string): Validator {
  let v = cache.get(key);
  if (!v) {
    v = makeValidator(schema, pointer);
    cache.set(key, v);
  }
  return v;
}

export const validate = {
  projectModel: (x: unknown) => run(cached("pm", projectModelSchema as SchemaDoc), x),
  parameterSet: (x: unknown) => run(cached("ps", parameterSetSchema as SchemaDoc), x),
  layerInventory: (x: unknown) => run(cached("li", layerInventorySchema as SchemaDoc), x),
  planPreview: (x: unknown) => run(cached("pp", planPreviewSchema as SchemaDoc), x),
  /** Validate against one definition of api.schema.json, for example api("CreateProjectRequest", body). */
  api: (def: string, x: unknown) => run(cached(`api:${def}`, apiSchema as SchemaDoc, `/$defs/${def}`), x),
};

export const regulationPresets = { [malagaRules.id]: malagaRules } as const;

/** The default parameter set: the 3-bedroom preset with the Malaga PGOU + CTE rules filled in. */
export function defaultParameterSet(): ParameterSet {
  const set = structuredClone(preset3bed) as unknown as ParameterSet;
  set.regulations.rules = structuredClone(malagaRules.rules) as unknown as Rule[];
  return set;
}

export const ROLE_LABELS: Record<import("./types").LayerRole, string> = {
  apartment_boundary: "Apartment boundary",
  exterior_wall: "Exterior wall",
  load_bearing: "Load-bearing wall / column",
  movable_partition: "Movable partition",
  window: "Window",
  entrance_door: "Entrance door",
  interior_door: "Interior door",
  shaft: "Shaft / riser",
  room_label: "Room label",
  ignore: "Ignore",
};
